import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Mono } from "../components/ui.jsx";

// In-app docs article for pointing a custom domain at a DebutDeploy service.
// Linked from the Add Custom Domain wizard ("Read the docs →"). Deliberately prints no IP:
// services run on different servers, so the exact records come from each service's page.
export default function DocsCustomDomains() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-8" style={{ color: "var(--text)" }}>
      <Link to="/" className="mb-4 inline-flex items-center gap-1.5 text-[13px]" style={{ color: "var(--text-muted)" }}>
        <ArrowLeft className="h-4 w-4" /> Back
      </Link>

      <h1 className="text-2xl font-semibold">Custom Domains</h1>
      <p className="mt-2 text-[14px]" style={{ color: "var(--text-muted)" }}>
        Point your own domain (e.g. <Mono>example.com</Mono>) at a service. DebutDeploy issues and
        auto-renews a free TLS certificate once your DNS points here.
      </p>

      <Section title="1. Add the domain in the panel">
        <p>
          Open your service → <b>Settings</b> → <b>Custom Domains</b> → <b>Add Custom Domain</b>. Enter your
          hostname and click <b>Add Domain</b>. A root domain such as <Mono>example.com</Mono> also gets{" "}
          <Mono>www.example.com</Mono>. A subdomain such as <Mono>app.example.com</Mono> is added on its own.
          Your free service subdomain (shown on the service page) stays in place.
        </p>
        <p className="mt-3">
          You don't have to set up DNS first. If your DNS isn't pointing at the service yet, the domain is
          saved as <b>waiting for DNS</b> and connected automatically as soon as it is — nothing else to click.
        </p>
      </Section>

      <Section title="2. Configure DNS with your provider">
        <p className="mb-3">
          The exact records for each service are shown in that service's <b>Custom Domains</b> section —
          in the wizard right after you add the domain, and any time afterwards under{" "}
          <b>Or add records manually</b> on the domain's row. Copy the values from there: services run on
          different servers, so the IP is specific to the service. The records look like this:
        </p>
        <table className="w-full text-[13px]" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ color: "var(--text-muted)", textAlign: "left" }}>
              <Th>Type</Th><Th>Name</Th><Th>Value</Th>
            </tr>
          </thead>
          <tbody>
            <Row type="A" host="@ (root) or your subdomain" value="the service's server IP, as shown on its page" />
            <Row type="CNAME" host="www (root domains only)" value="your root domain, e.g. example.com" />
          </tbody>
        </table>
        <p className="mt-3 text-[13px]" style={{ color: "var(--text-muted)" }}>
          <b>Things that stop a domain working:</b> a second <Mono>A</Mono> record left over from a previous
          host; an IPv6 <Mono>AAAA</Mono> record (it breaks certificate issuance — remove it); or a{" "}
          <Mono>CAA</Mono> record that doesn't allow Let's Encrypt. The panel flags each of these on the
          domain's row when it finds them. Never put an IP in a <Mono>CNAME</Mono> value.
        </p>
      </Section>

      <Section title="3. Check the status">
        <p>
          In the wizard, click <b>Verify</b> to check whether each hostname resolves to the service yet.
          DNS changes can take up to 24 hours to propagate (often minutes). Each domain in the list shows
          three statuses, and the list re-checks itself while anything is still pending:
        </p>
        <ul className="mt-3 list-disc pl-5">
          <li><b>DNS</b> — your records point at this service's server. Until they do, the row shows what the domain resolves to now and what it should be.</li>
          <li className="mt-1"><b>Routing</b> — the service is serving the domain. If DNS is correct but this isn't, click <b>Redeploy to fix</b>.</li>
          <li className="mt-1"><b>Certificate</b> — a Let's Encrypt certificate has been issued. This follows DNS and routing automatically, usually within a few minutes; if it stays pending, click <b>Retry certificate</b>.</li>
        </ul>
        <p className="mt-3">
          If you're migrating from another host, remove the domain there after cutover so it isn't holding
          the old routing.
        </p>
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-7">
      <h2 className="mb-2 text-[15px] font-semibold">{title}</h2>
      <div className="text-[14px] leading-relaxed" style={{ color: "var(--text)" }}>{children}</div>
    </div>
  );
}

function Th({ children }) {
  return <th className="border-b py-1.5 pr-4 font-medium" style={{ borderColor: "var(--border)" }}>{children}</th>;
}

function Row({ type, host, value }) {
  return (
    <tr>
      <td className="border-b py-2 pr-4" style={{ borderColor: "var(--border)" }}><Mono>{type}</Mono></td>
      <td className="border-b py-2 pr-4" style={{ borderColor: "var(--border)" }}>{host}</td>
      <td className="border-b py-2 pr-4" style={{ borderColor: "var(--border)" }}>{value}</td>
    </tr>
  );
}
